import { useCallback, useEffect, useRef, useState } from 'react'
import { useBackend } from '../../lib/session'
import { asUploadableAudio, assertUploadableAudio, formatDuration, formatLimit, type CapturedAudio } from '../../lib/audio'
import { analyseAudio } from '../../lib/peaks'
import { errorText } from '../ui'

// "Upload a file instead": a picked audio file checked against what the
// writing-audio bucket takes, measured (length and waveform), and uploaded.
// No sheet: the card and the dock each keep a hidden file input and show
// progress and errors in place.

export interface AudioFileUpload {
  /** "Reading the file…" / "Uploading…" while it works. */
  busy: string | null
  error: string | null
  clearError(): void
  /** Resolves to the uploaded audio, or null (the error is set). */
  upload(file: File | undefined | null): Promise<CapturedAudio | null>
}

export function useAudioFileUpload(maxSeconds: number): AudioFileUpload {
  const backend = useBackend()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => void (alive.current = false)
  }, [])

  const upload = useCallback(
    async (file: File | undefined | null): Promise<CapturedAudio | null> => {
      if (!file) return null
      setError(null)
      let blob: Blob
      try {
        blob = asUploadableAudio(file)
        assertUploadableAudio(blob)
      } catch (e) {
        setError(errorText(e))
        return null
      }
      setBusy('Reading the file…')
      try {
        const a = await analyseAudio(blob)
        if (!alive.current) return null
        if (a.duration > maxSeconds + 1) {
          setError(`That runs ${formatDuration(a.duration)}. The limit here is ${formatLimit(maxSeconds)}.`)
          return null
        }
        setBusy('Uploading…')
        const src = await backend.uploadAudio(blob)
        if (!alive.current) return null
        return { src, duration: a.duration, peaks: a.peaks, recordedAt: new Date().toISOString() }
      } catch (e) {
        if (alive.current) setError(errorText(e))
        return null
      } finally {
        if (alive.current) setBusy(null)
      }
    },
    [backend, maxSeconds],
  )

  return { busy, error, clearError: useCallback(() => setError(null), []), upload }
}
